/**
 * Endpoints Page
 * Manage REST API endpoints and connections
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Database,
  Play,
  Pause,
  Trash2,
  Edit,
  RefreshCw,
  Check,
  AlertCircle,
  Download,
  Eye,
  X,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { Modal } from '../components/ui';
import { EndpointForm } from '../components/forms';
import { endpointsApi, dataApi } from '../api/client';
import { RestEndpoint } from '../types';

export function EndpointsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<RestEndpoint | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [viewingEndpoint, setViewingEndpoint] = useState<RestEndpoint | null>(null);
  const [endpointData, setEndpointData] = useState<Record<string, unknown>[] | null>(null);
  const [dataLoading, setDataLoading] = useState(false);

  const {
    data: endpoints,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['endpoints'],
    queryFn: endpointsApi.list,
  });

  const filteredEndpoints = endpoints?.filter(
    (e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const createMutation = useMutation({
    mutationFn: (data: Partial<RestEndpoint>) => endpointsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
      setIsModalOpen(false);
      setEditingEndpoint(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RestEndpoint> }) =>
      endpointsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
      setIsModalOpen(false);
      setEditingEndpoint(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => endpointsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => endpointsApi.test(id),
    onMutate: (id) => {
      setTestingId(id);
      setTestResult(null);
    },
    onSuccess: (result) => {
      setTestResult(result);
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
    onError: (error: Error) => {
      setTestResult({ success: false, message: error.message });
    },
    onSettled: () => {
      setTestingId(null);
    },
  });

  const fetchMutation = useMutation({
    mutationFn: (id: string) => endpointsApi.fetch(id, {}),  // Don't limit - use endpoint's max_records
    onMutate: (id) => {
      setFetchingId(id);
      setTestResult(null);
    },
    onSuccess: (result: { success: boolean; message: string; record_count?: number; pages_fetched?: number }) => {
      // Enhanced message with pagination info
      let message = result.message;
      if (result.pages_fetched && result.pages_fetched > 1) {
        message = `Successfully fetched ${result.record_count} records from ${result.pages_fetched} pages`;
      }
      setTestResult({ ...result, message });
      queryClient.invalidateQueries({ queryKey: ['endpoints'] });
    },
    onError: (error: Error) => {
      setTestResult({ success: false, message: error.message });
    },
    onSettled: () => {
      setFetchingId(null);
    },
  });

  const handleAdd = () => {
    setEditingEndpoint(null);
    setIsModalOpen(true);
  };

  const handleEdit = (endpoint: RestEndpoint) => {
    setEditingEndpoint(endpoint);
    setIsModalOpen(true);
  };

  const handleDelete = (endpoint: RestEndpoint) => {
    if (confirm(`Are you sure you want to delete the endpoint "${endpoint.name}"?`)) {
      deleteMutation.mutate(endpoint.id);
    }
  };

  const handleSubmit = (data: Partial<RestEndpoint>) => {
    if (editingEndpoint) {
      updateMutation.mutate({ id: editingEndpoint.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleTest = (endpoint: RestEndpoint) => {
    testMutation.mutate(endpoint.id);
  };

  const handleFetch = (endpoint: RestEndpoint) => {
    fetchMutation.mutate(endpoint.id);
  };

  const handleViewData = async (endpoint: RestEndpoint) => {
    setViewingEndpoint(endpoint);
    setDataLoading(true);
    setEndpointData(null);
    try {
      const result = await dataApi.getData(endpoint.id, { page_size: 5000 });
      setEndpointData(result.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      setEndpointData([]);
    } finally {
      setDataLoading(false);
    }
  };

  const closeDataModal = () => {
    setViewingEndpoint(null);
    setEndpointData(null);
  };

  return (
    <Layout
      title="Endpoints"
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              REST Endpoints
            </h1>
            <p className="text-gray-500 mt-1">
              Configure data sources from Infor LN API
            </p>
          </div>
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Endpoint
          </button>
        </div>

        {/* Test Result Toast */}
        {testResult && (
          <div
            className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            {testResult.success ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <span>{testResult.message}</span>
            <button
              onClick={() => setTestResult(null)}
              className="ml-auto text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-600">
              Failed to load endpoints. Please try again.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && filteredEndpoints?.length === 0 && (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No endpoints configured
            </h3>
            <p className="text-gray-500 mb-4">
              Add your first REST endpoint to start fetching data
            </p>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf]"
            >
              <Plus className="w-5 h-5" />
              Add Endpoint
            </button>
          </div>
        )}

        {/* Endpoints Table */}
        {!isLoading && !error && filteredEndpoints && filteredEndpoints.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Endpoint
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Path
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Fetched
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEndpoints.map((endpoint) => (
                  <tr key={endpoint.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded">
                          <Database className="w-4 h-4 text-[#45bdd6]" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{endpoint.name}</p>
                          {endpoint.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">
                              {endpoint.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {endpoint.path}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                          endpoint.is_active
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {endpoint.is_active ? (
                          <>
                            <Check className="w-3 h-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <Pause className="w-3 h-3" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {endpoint.last_fetched_at
                        ? new Date(endpoint.last_fetched_at).toLocaleString('en-GB')
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleTest(endpoint)}
                          disabled={testingId === endpoint.id}
                          className="p-2 text-gray-400 hover:text-[#45bdd6] hover:bg-blue-50 rounded disabled:opacity-50"
                          title="Test Connection"
                        >
                          {testingId === endpoint.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleFetch(endpoint)}
                          disabled={fetchingId === endpoint.id}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                          title="Fetch Data"
                        >
                          {fetchingId === endpoint.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleViewData(endpoint)}
                          className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                          title="View Data"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(endpoint)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(endpoint)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingEndpoint(null);
        }}
        title={editingEndpoint ? 'Edit Endpoint' : 'New Endpoint'}
        size="lg"
      >
        <EndpointForm
          endpoint={editingEndpoint}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingEndpoint(null);
          }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Data View Modal */}
      {viewingEndpoint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Data: {viewingEndpoint.name}
                </h2>
                <p className="text-sm text-gray-500">{endpointData?.length || 0} records</p>
              </div>
              <button
                onClick={closeDataModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {dataLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-[#45bdd6] animate-spin" />
                  <span className="ml-3 text-gray-500">Loading data...</span>
                </div>
              ) : endpointData && endpointData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {Object.keys(endpointData[0]).map((key) => (
                          <th
                            key={key}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap border-b"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {endpointData.map((record, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          {Object.values(record).map((value, colIdx) => (
                            <td
                              key={colIdx}
                              className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate"
                              title={String(value)}
                            >
                              {value === null
                                ? <span className="text-gray-400 italic">null</span>
                                : typeof value === 'object'
                                ? JSON.stringify(value)
                                : String(value)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    No data available. Click "Fetch Data" to load data.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => handleFetch(viewingEndpoint)}
                disabled={fetchingId === viewingEndpoint.id}
                className="flex items-center gap-2 px-4 py-2 text-[#45bdd6] hover:bg-blue-50 rounded-lg disabled:opacity-50"
              >
                {fetchingId === viewingEndpoint.id ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Refresh
              </button>
              <button
                onClick={closeDataModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
